import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { useCompanySettings } from "@/lib/companySettings";
import { type ImportBatchResponse, useImportBatch } from "@/lib/accounts";
import { usePublicPresets } from "@/lib/credentials";
import { useProxies } from "@/lib/proxies";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SimpleSelect, type SimpleSelectOption } from "@/components/ui/select";
import { Alert } from "@/components/ui/alert";
import { ProxyChoice, type ProxyMode } from "@/components/ProxyChoice";
import { ImportDropzone } from "@/components/accounts/ImportDropzone";
import { planImportFiles } from "@/components/accounts/importPlan";
import { WarmupSpeedPicker, type WarmupSpeed } from "@/components/accounts/WarmupSpeedPicker";
import { MetadataForm } from "@/components/accounts/MetadataForm";
import { type MetadataValues, emptyMetadata } from "@/components/accounts/metadata";

/** Lines that carry something — the backend skips blanks and `#` comments too. */
function countPastedLines(text: string): number {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith("#")).length;
}

/**
 * The one import dialog. Whatever the seller shipped — archives, a folder of
 * them, loose `.session` files, session strings in a text file — goes in here
 * and the form works out what it is. Everything that used to be an operator
 * decision with no knowable answer (which api-key preset? which endpoint?) is
 * either derived or tucked behind "advanced".
 */
export function ImportDialog({
  open,
  onOpenChange,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (res: ImportBatchResponse) => void;
}) {
  const { t } = useTranslation();
  const presets = usePublicPresets();
  const proxies = useProxies();
  const importBatch = useImportBatch();
  const settings = useCompanySettings();

  const [files, setFiles] = useState<File[]>([]);
  const [pasted, setPasted] = useState("");
  const [credId, setCredId] = useState("");
  const [proxyMode, setProxyMode] = useState<ProxyMode>("by_country");
  const [proxyId, setProxyId] = useState("");
  const [confirmSharedProxy, setConfirmSharedProxy] = useState(false);
  const [rotate2fa, setRotate2fa] = useState(true);
  const [startWarmup, setStartWarmup] = useState(true);
  const [warmupSpeed, setWarmupSpeed] = useState<WarmupSpeed>("natural");
  // null = follow the batch size (see `staggered` below); a click pins it.
  const [staggeredChoice, setStaggeredChoice] = useState<boolean | null>(null);
  const [notes, setNotes] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [meta, setMeta] = useState<MetadataValues>(emptyMetadata);
  const [error, setError] = useState<string | null>(null);

  // Warmup defaults come from Settings → Аккаунты; the operator can override
  // them per import.
  useEffect(() => {
    if (!open || !settings.data) return;
    setStartWarmup(settings.data.warmup.auto_start_on_import);
    setWarmupSpeed(settings.data.importing.default_warmup_speed);
    const src = settings.data.importing.default_source_label;
    if (src) setMeta((m) => ({ ...m, sourceLabel: m.sourceLabel || src }));
  }, [open, settings.data]);

  // Telegram Desktop is the credential most sessions were made under, and the
  // only correct one for tdata — so it is the fallback for rows whose metadata
  // doesn't name a key. Rows that DO name one still use their own.
  useEffect(() => {
    if (credId || !presets.data?.length) return;
    const desktop = presets.data.find((p) => /desktop/i.test(p.label));
    setCredId((desktop ?? presets.data[0]).id);
  }, [credId, presets.data]);

  const plan = useMemo(() => planImportFiles(files), [files]);
  const pastedCount = countPastedLines(pasted);
  const accountCount = plan.archives.length + plan.sessions.length + pastedCount;
  // A phone number is known up front for archives and for a `.session` that
  // came with its `<phone>.json` — those can be routed to a same-country proxy.
  // With nothing dropped yet there is nothing to warn about.
  const byCountrySupported =
    plan.archives.length > 0 || plan.pairedMetas.length > 0 || accountCount === 0;
  const staggered = staggeredChoice ?? accountCount > 1;

  const reset = () => {
    setFiles([]);
    setPasted("");
    setCredId("");
    setProxyMode("by_country");
    setProxyId("");
    setConfirmSharedProxy(false);
    setRotate2fa(true);
    setStartWarmup(true);
    setWarmupSpeed("natural");
    setStaggeredChoice(null);
    setNotes("");
    setShowAdvanced(false);
    setMeta(emptyMetadata);
    setError(null);
  };

  const close = () => {
    onOpenChange(false);
    // Defer so the closing animation doesn't flash an empty form.
    setTimeout(reset, 200);
  };

  const credOptions: SimpleSelectOption[] = (presets.data ?? []).map((p) => ({
    value: p.id,
    label: `${p.label}${p.lang_code ? ` · ${p.lang_code}` : ""}`,
  }));

  const handle = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (accountCount === 0) {
      setError(t("accounts.import.error_nothing"));
      return;
    }
    if (!credId) {
      setError(t("accounts.error_credential_required"));
      return;
    }
    try {
      const res = await importBatch.mutateAsync({
        api_credential_id: credId,
        api_credential_kind: "auto",
        proxy_id: proxyMode === "specific" ? proxyId || null : null,
        proxy_random: proxyMode === "any",
        proxy_country_match: proxyMode === "by_country",
        confirm_shared_proxy: confirmSharedProxy,
        // The protection runs as warmup steps: with warmup off nothing would
        // ever be scheduled, so it is not requested (and the switch says so).
        rotate_2fa: startWarmup && rotate2fa,
        seller_takeover_defense: startWarmup && rotate2fa,
        start_warmup: startWarmup,
        warmup_speed: warmupSpeed,
        staggered,
        notes: notes.trim() || undefined,
        source_label: meta.sourceLabel.trim() || undefined,
        source_url: meta.sourceUrl.trim() || undefined,
        purchase_cost_usd: meta.purchaseCost.trim() || undefined,
        purchase_date: meta.purchaseDate || undefined,
        seller_account_age_days: meta.sellerAge ? Number(meta.sellerAge) : undefined,
        country_code: meta.countryCode.trim().toUpperCase().slice(0, 2) || undefined,
        // Strays are left behind on purpose — the backend ignores them anyway,
        // and a seller's folder can hold a lot of them.
        files: [...plan.archives, ...plan.sessions, ...plan.pairedMetas],
        session_strings: pasted.trim() || undefined,
      });
      onComplete(res);
      close();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("accounts.error_unknown"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent size="xl">
        <form onSubmit={handle} className="flex min-h-0 flex-col">
          <DialogHeader>
            <DialogTitle>{t("accounts.import.title")}</DialogTitle>
            <DialogDescription>{t("accounts.import.subtitle")}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <ImportDropzone files={files} onChange={setFiles} />

            <div className="space-y-1.5">
              <Label>{t("accounts.import.paste_label")}</Label>
              <textarea
                rows={2}
                placeholder="1AbCdEf..."
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                className="w-full rounded-md border border-border bg-bg-secondary px-3 py-2 font-mono text-xs text-fg-primary"
              />
              <p className="text-xs text-fg-muted">{t("accounts.import.paste_hint")}</p>
            </div>

            <ImportPlanSummary
              archives={plan.archives.length}
              sessions={plan.sessions.length}
              pasted={pastedCount}
              metas={plan.pairedMetas.length}
              strays={plan.strays.length}
            />

            <ProxyChoice
              mode={proxyMode}
              onMode={setProxyMode}
              proxyId={proxyId}
              onProxyId={setProxyId}
              proxies={proxies.data ?? []}
              byCountrySupported={byCountrySupported}
              confirmShared={confirmSharedProxy}
              onConfirmShared={setConfirmSharedProxy}
              incomingCount={accountCount}
            />

            <div className="space-y-3 rounded-xl border border-border bg-bg-primary/40 p-3">
              <Switch
                checked={startWarmup && rotate2fa}
                onCheckedChange={setRotate2fa}
                disabled={!startWarmup}
                label={t("accounts.import.rotate_2fa")}
                description={
                  startWarmup
                    ? t("accounts.import.rotate_2fa_hint")
                    : t("accounts.import.rotate_2fa_needs_warmup")
                }
              />
              <Switch
                checked={startWarmup}
                onCheckedChange={setStartWarmup}
                label={t("accounts.field.start_warmup")}
                description={t("accounts.field.start_warmup_hint")}
              />
              {startWarmup && (
                <div className="pl-12">
                  <WarmupSpeedPicker
                    value={warmupSpeed}
                    onChange={setWarmupSpeed}
                    name="import-warmup-speed"
                  />
                </div>
              )}
              <Switch
                checked={staggered}
                onCheckedChange={(v) => setStaggeredChoice(v)}
                label={t("accounts.import.staggered")}
                description={
                  staggeredChoice === null && accountCount > 1
                    ? t("accounts.import.staggered_auto_hint")
                    : t("accounts.import.staggered_hint")
                }
              />
            </div>

            <div className="rounded-xl border border-border">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="flex w-full items-center justify-between px-3 py-2.5 text-sm font-medium text-fg-primary"
              >
                <span>{t("accounts.import.advanced")}</span>
                <span className="text-fg-muted">{showAdvanced ? "–" : "+"}</span>
              </button>
              {showAdvanced && (
                <div className="space-y-3 border-t border-border p-3">
                  <div className="space-y-1.5">
                    <Label>{t("accounts.import.credential_fallback")}</Label>
                    <SimpleSelect
                      options={credOptions}
                      value={credId}
                      onValueChange={setCredId}
                      placeholder={t("accounts.field.credential_select")}
                    />
                    <p className="text-xs text-fg-muted">
                      {t("accounts.import.credential_fallback_hint")}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("accounts.field.notes")}</Label>
                    <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </div>
                  <div className="space-y-3 border-t border-border pt-3">
                    <p className="text-xs text-fg-muted">
                      {t("accounts.field.metadata_hint_batch")}
                    </p>
                    <MetadataForm values={meta} onChange={setMeta} />
                  </div>
                </div>
              )}
            </div>

            {(presets.data?.length ?? 0) === 0 && !presets.isLoading && (
              <Alert tone="warning" title={t("accounts.field.credential_preset_empty")} />
            )}
            {error && <p className="break-all text-sm text-danger">{error}</p>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={importBatch.isPending} disabled={accountCount === 0}>
              {accountCount === 0
                ? t("accounts.import.run_empty")
                : t("accounts.import.run", { count: accountCount })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Say what we understood from the drop before anything is sent. */
function ImportPlanSummary({
  archives,
  sessions,
  pasted,
  metas,
  strays,
}: {
  archives: number;
  sessions: number;
  pasted: number;
  metas: number;
  strays: number;
}) {
  const { t } = useTranslation();
  const total = archives + sessions + pasted;
  if (total === 0 && strays === 0) return null;

  const parts = [
    archives > 0 ? t("accounts.import.plan.archives", { count: archives }) : null,
    sessions > 0 ? t("accounts.import.plan.sessions", { count: sessions }) : null,
    pasted > 0 ? t("accounts.import.plan.pasted", { count: pasted }) : null,
    metas > 0 ? t("accounts.import.plan.metas", { count: metas }) : null,
  ].filter(Boolean);

  return (
    <div className="rounded-lg border border-border bg-bg-primary/40 px-3 py-2 text-xs">
      <p className="font-medium text-fg-primary">
        {t("accounts.import.plan.total", { count: total })}
      </p>
      {parts.length > 0 && <p className="mt-0.5 text-fg-muted">{parts.join(" · ")}</p>}
      {strays > 0 && (
        <p className="mt-0.5 text-fg-muted">
          {t("accounts.import.plan.strays", { count: strays })}
        </p>
      )}
    </div>
  );
}
